import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { FaStore, FaMapMarkerAlt, FaShoppingCart, FaListAlt, FaUser, FaCog } from 'react-icons/fa';

export default function BottomNavigation() {
  const location = useLocation();
  const { user } = useAuth();
  const { items } = useCart();

  const cartItemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const baselineHeightRef = useRef<number>(0);

  const isNative = Capacitor.isNativePlatform();
  const darkModeEnabled =
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

  useEffect(() => {
    if (!isNative) return;
    const viewport = window.visualViewport;
    if (!viewport) return;

    baselineHeightRef.current = viewport.height;
    const detectKeyboard = () => {
      if (!baselineHeightRef.current) {
        baselineHeightRef.current = viewport.height;
      }
      const delta = baselineHeightRef.current - viewport.height;
      setKeyboardVisible(delta > 140);
    };

    const onFocusIn = () => detectKeyboard();
    const onFocusOut = () => {
      window.setTimeout(() => {
        detectKeyboard();
      }, 120);
    };

    viewport.addEventListener('resize', detectKeyboard);
    window.addEventListener('focusin', onFocusIn);
    window.addEventListener('focusout', onFocusOut);
    return () => {
      viewport.removeEventListener('resize', detectKeyboard);
      window.removeEventListener('focusin', onFocusIn);
      window.removeEventListener('focusout', onFocusOut);
    };
  }, [isNative]);

  // Show bottom navigation whenever the user is logged in (web and native)
  if (!user) {
    return null;
  }

  // On native app, hide bottom nav while keyboard is open so it doesn't float above keyboard.
  if (isNative && keyboardVisible) {
    return null;
  }

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav
      className={`consumer-bottom-nav fixed bottom-0 left-0 right-0 border-t shadow-lg z-50 ${
        darkModeEnabled
          ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
          : 'bg-[#f8efe5] border-[#dcc2a8]'
      }`}
      style={{
        paddingBottom: 'env(safe-area-inset-bottom)',
        ...(isNative
          ? {
              backgroundColor: darkModeEnabled ? '#1e120e' : '#f3e7da',
              borderTopColor: darkModeEnabled ? '#2d1811' : '#d3b496',
            }
          : {}),
      }}
    >
      <div className="flex items-center justify-around h-16">
        <Link
          to="/cafes"
          className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
            isActive('/cafes')
              ? 'text-amber-700 dark:text-amber-700'
              : 'text-gray-600 dark:text-gray-400'
          }`}
        >
          <FaStore className="text-xl mb-1" />
          <span className="text-xs font-medium">Cafes</span>
        </Link>

        <Link
          to="/route"
          className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
            isActive('/route')
              ? 'text-amber-700 dark:text-amber-400'
              : 'text-gray-600 dark:text-gray-400'
          }`}
        >
          <FaMapMarkerAlt className="text-xl mb-1" />
          <span className="text-xs font-medium">Route</span>
        </Link>

        <Link
          to="/cart"
          className={`relative flex flex-col items-center justify-center flex-1 h-full transition-colors ${
            isActive('/cart')
              ? 'text-amber-700 dark:text-amber-400'
              : 'text-gray-600 dark:text-gray-400'
          }`}
        >
          <FaShoppingCart className="text-xl mb-1" />
          {cartItemCount > 0 && (
            <span className="absolute top-1 right-1/4 bg-gradient-to-r from-red-500 to-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
              {cartItemCount}
            </span>
          )}
          <span className="text-xs font-medium">Cart</span>
        </Link>

        <Link
          to="/orders"
          className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
            isActive('/orders')
              ? 'text-amber-700 dark:text-amber-400'
              : 'text-gray-600 dark:text-gray-400'
          }`}
        >
          <FaListAlt className="text-xl mb-1" />
          <span className="text-xs font-medium">Orders</span>
        </Link>

        <Link
          to="/profile"
          className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
            isActive('/profile')
              ? 'text-amber-700 dark:text-amber-400'
              : 'text-gray-600 dark:text-gray-400'
          }`}
        >
          <FaUser className="text-xl mb-1" />
          <span className="text-xs font-medium">Profile</span>
        </Link>

        <Link
          to="/settings"
          className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
            isActive('/settings')
              ? 'text-amber-700 dark:text-amber-400'
              : 'text-gray-600 dark:text-gray-400'
          }`}
        >
          <FaCog className="text-xl mb-1" />
          <span className="text-xs font-medium">Settings</span>
        </Link>
      </div>
    </nav>
  );
}

